import { useState, useCallback, useEffect } from 'react';
import { ICheckboxProps, ITreeNode } from '../../common/types/commonTypes';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceData } from '../../pages/deviceManagement/deviceSlice';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { selector as groupSelector } from '../../pages/deviceManagement/groupSlice';
import { useSpinner } from '../../customHooks';
import React from 'react';
import {
  IGroupAlerts,
  ISelectGroupState,
} from '../../common/types/alertsTypes';
import { getAllNodeValues } from './util';
import { IDeviceApiState } from '../../common/types/devicesTypes';

interface IProps {
  nodes: ITreeNode[];
  onDataFromTreeView?: (selectedGroup: IGroupAlerts) => void;
  isModalOrg?: boolean;
  setIsEmptyGroupShown?: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  isSearching?: boolean;
  isMultiSelect?: boolean;
  selectGroupState?: ISelectGroupState;
  setSelectGroupState?: (state) => void;
  checkedBox?: string[];
  reload?: boolean;
  setReload?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useTreeView = (props?: IProps) => {
  const {
    nodes,
    setIsEmptyGroupShown,
    setSearchTerm,
    isSearching,
    isModalOrg,
    isMultiSelect,
    setSelectGroupState,
    selectGroupState,
    checkedBox,
    reload,
    setReload,
  } = props;
  const dispatch: ThunkDispatch<IDeviceApiState, undefined, Action> =
    useDispatch();
  const allGroups: string[] = getAllNodeValues(nodes, true, true);
  const [checked, setChecked] = useState<string[]>(
    checkedBox ? checkedBox : selectGroupState?.checked
  );
  const [expanded, setExpanded] = useState<string[]>([]);
  const getGroupStatus = useSelector(groupSelector).status;
  const [selectedGroup, setSelectedGroup] = useState<any>(
    selectGroupState?.groups
  );
  useSpinner(getGroupStatus);
  useEffect(() => {
    if (isSearching) {
      setChecked([]);
    }
  }, [isSearching]);

  useEffect(() => {
    if (selectGroupState && reload) {
      setChecked([]);
      setExpanded([]);
      setSelectedGroup([]);
      setSelectAllChecked(false);
      setReload(false);
    }
  }, [selectGroupState]);

  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(
    selectedGroup?.length == allGroups?.length
  );

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked: boolean = event.target.checked;
    if (isChecked) {
      const allNodeValues: string[] = getAllNodeValues(nodes, true);
      setSelectAllChecked(true);
      setSelectedGroup(allGroups);
      setSelectGroupState({
        ...selectGroupState,
        groups: allGroups,
        checked: allNodeValues,
      });
      setChecked(allNodeValues);
    } else {
      setSelectAllChecked(false);
      setSelectGroupState({
        ...selectGroupState,
        groups: [],
        checked: [],
      });
      setChecked([]);
      setSelectedGroup([]);
    }
  };

  const handleCheck = useCallback(
    (organizationChecked: string[], targetNode: ITreeNode) => {
      isModalOrg
        ? handleModalOrg(organizationChecked, targetNode)
        : handleTableOrg(organizationChecked, targetNode);
    },
    []
  );

  const handleModalOrg = (
    organizationChecked: string[],
    targetNode: ITreeNode
  ) => {
    isMultiSelect
      ? multiCheckboxSelect(targetNode)
      : singleCheckboxSelect(organizationChecked, targetNode);
  };

  const singleCheckboxSelect = (
    organizationChecked: string[],
    targetNode: ITreeNode
  ) => {
    if (organizationChecked.length) {
      setChecked([targetNode.value]);
      setSelectedGroup({
        id: targetNode.value,
        displayName: targetNode.label,
      });
    } else {
      setChecked([]);
      setSelectedGroup([]);
    }
  };
  const multiCheckboxSelect = (targetNode: ITreeNode) => {
    setChecked((prevChecked) => {
      const isChecked: boolean = prevChecked.includes(targetNode.value);
      if (isChecked) {
        return prevChecked.filter((value) => value !== targetNode.value);
      } else {
        return [...prevChecked, targetNode.value];
      }
    });

    setSelectedGroup((prevSelectedGroup) => {
      const isSelected: boolean = prevSelectedGroup.some(
        (node) =>
          node.value === targetNode.value || node.id === targetNode.value
      );
      if (!isSelected) {
        const newSelectedGroups: IGroupAlerts[] = [
          ...prevSelectedGroup,
          {
            id: targetNode.value,
            displayName: targetNode.label,
          },
        ];
        if (newSelectedGroups.length == allGroups.length) {
          setSelectAllChecked(true);
        }
        return newSelectedGroups;
      } else {
        setSelectAllChecked(false);
        return prevSelectedGroup.filter((node) => {
          if (node.value) {
            return node.value !== targetNode.value;
          } else if (node.id) {
            return node.id !== targetNode.value;
          }
        });
      }
    });
  };

  const handleTableOrg = useCallback(
    (organizationChecked: string[], targetNode: ITreeNode) => {
      if (organizationChecked.length) {
        setIsEmptyGroupShown(false);
        setChecked([targetNode.value]);
        setSearchTerm('');
        dispatch(
          fetchDeviceData(targetNode.value)
        );
      } else {
        setChecked([]);
        dispatch(fetchDeviceData(''));
        setIsEmptyGroupShown(true);
      }
    },
    [dispatch]
  );

  const handleExpand = (newExpanded: string[]) => {
    setExpanded(newExpanded);
  };

  const treeViewProps: ICheckboxProps = {
    nodes: nodes,
    checked,
    expanded,
    onCheck: handleCheck,
    onExpand: handleExpand,
    showNodeTitles: false,
    noCascade: true,
  };

  return {
    treeViewProps,
    selectedGroup,
    handleSelectAllChange,
    selectAllChecked,
  };
};

export default useTreeView;

useTreeView.defaulProps = {
  isModalOrg: false,
};
